// "use client";

// import React, {
//   createContext,
//   useCallback,
//   useContext,
//   useEffect,
//   useMemo,
//   useState,
// } from "react";
// import toast from "react-hot-toast";
// import { readCart, writeCart, clearCart } from "./storage";
// import type { CartLine } from "./types";

// type AddInput = { id: number; name: string; price: number; imageUrl?: string };

// type CartContextValue = {
//   lines: CartLine[];
//   add: (item: AddInput, qty?: number) => void;
//   remove: (id: number) => void;
//   setQty: (id: number, qty: number) => void;
//   toggle: (id: number, sel?: boolean) => void;
//   selectAll: (sel: boolean) => void;
//   clearAll: () => void;

//   subtotal: number;
//   vat: (ratePct?: number) => number; // default 5%
//   delivery: (flat?: number) => number; // default 100
//   total: (ratePct?: number, flat?: number) => number;

//   selectedCount: number;
//   itemCount: number; // total quantity (all lines)
// };

// const CartCtx = createContext<CartContextValue | null>(null);

// export function CartProvider({ children }: { children: React.ReactNode }) {
//   const [lines, setLines] = useState<CartLine[]>([]);

//   // load once (CSR only)
//   useEffect(() => {
//     setLines(readCart());
//     const onChange = () => setLines(readCart());
//     window.addEventListener("storage", onChange);
//     window.addEventListener("cartUpdated", onChange as EventListener);
//     return () => {
//       window.removeEventListener("storage", onChange);
//       window.removeEventListener("cartUpdated", onChange as EventListener);
//     };
//   }, []);

//   // helpers
//   const persist = useCallback((next: CartLine[]) => {
//     setLines(next);
//     writeCart(next);
//   }, []);

//   const add = useCallback((item: AddInput, qty = 1) => {
//     if (qty <= 0) return;
//     setLines((prev) => {
//       const next = [...prev];
//       const i = next.findIndex((l) => l.id === item.id);
//       if (i >= 0) {
//         next[i] = {
//           ...next[i],
//           quantity: next[i].quantity + qty,
//           selected: true,
//         };
//       } else {
//         next.unshift({
//           id: item.id,
//           name: item.name,
//           price: Number(item.price),
//           imageUrl: item.imageUrl,
//           quantity: qty,
//           selected: true,
//         });
//       }
//       writeCart(next);
//       return next;
//     });
//     toast.success("Added to cart");
//   }, []);

//   const remove = useCallback(
//     (id: number) => {
//       const next = lines.filter((l) => l.id !== id);
//       persist(next);
//       toast.success("Removed");
//     },
//     [lines, persist]
//   );

//   const setQty = useCallback(
//     (id: number, qty: number) => {
//       if (qty < 1) qty = 1;
//       const next = lines.map((l) =>
//         l.id === id ? { ...l, quantity: qty } : l
//       );
//       persist(next);
//     },
//     [lines, persist]
//   );

//   const toggle = useCallback(
//     (id: number, sel?: boolean) => {
//       const next = lines.map((l) =>
//         l.id === id ? { ...l, selected: sel ?? !l.selected } : l
//       );
//       persist(next);
//     },
//     [lines, persist]
//   );

//   const selectAll = useCallback(
//     (sel: boolean) => {
//       persist(lines.map((l) => ({ ...l, selected: sel })));
//     },
//     [lines, persist]
//   );

//   const clearAll = useCallback(() => {
//     clearCart();
//     setLines([]);
//   }, []);

//   const subtotal = useMemo(
//     () =>
//       lines
//         .filter((l) => l.selected)
//         .reduce((s, l) => s + l.price * l.quantity, 0),
//     [lines]
//   );
//   const vat = useCallback(
//     (ratePct = 5) => (subtotal * ratePct) / 100,
//     [subtotal]
//   );
//   const delivery = useCallback(
//     (flat = 100) => (subtotal > 0 ? flat : 0),
//     [subtotal]
//   );
//   const total = useCallback(
//     (ratePct = 5, flat = 100) => subtotal + vat(ratePct) + delivery(flat),
//     [subtotal, vat, delivery]
//   );

//   const selectedCount = useMemo(
//     () => lines.filter((l) => l.selected).length,
//     [lines]
//   );
//   const itemCount = useMemo(
//     () => lines.reduce((s, l) => s + l.quantity, 0),
//     [lines]
//   );

//   const value: CartContextValue = {
//     lines,
//     add,
//     remove,
//     setQty,
//     toggle,
//     selectAll,
//     clearAll,
//     subtotal,
//     vat,
//     delivery,
//     total,
//     selectedCount,
//     itemCount,
//   };

//   return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
// }

// export function useCart() {
//   const ctx = useContext(CartCtx);
//   if (!ctx) throw new Error("useCart must be used within <CartProvider>");
//   return ctx;
// }
// lib/cart/useCart.tsx
"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { readCart, writeCart, clearCart } from "./storage";
import type { CartLine } from "./types";

type AddInput = { id: number; name: string; price: number; imageUrl?: string };

export type DeliveryZone =
  | "banani"
  | "gulshan_1"
  | "gulshan_2"
  | "outside_gulshan_banani"
  | "outside_dhaka"
  | "";

export const DELIVERY_ZONES: {
  value: DeliveryZone;
  label: string;
  fee: number | null;
}[] = [
  { value: "banani", label: "Banani", fee: 100 },
  { value: "gulshan_1", label: "Gulshan 1", fee: 150 },
  { value: "gulshan_2", label: "Gulshan 2", fee: 100 },
  {
    value: "outside_gulshan_banani",
    label: "Uttara / Dhanmondi / Azimpur & others",
    fee: 350,
  },
  { value: "outside_dhaka", label: "Outside Dhaka", fee: null },
];

export function getDeliveryFee(zone: DeliveryZone): number | null {
  if (!zone) return 0;
  return DELIVERY_ZONES.find((z) => z.value === zone)?.fee ?? 0;
}

type CartContextValue = {
  lines: CartLine[];
  add: (item: AddInput, qty?: number) => void;
  remove: (id: number) => void;
  setQty: (id: number, qty: number) => void;
  toggle: (id: number, sel?: boolean) => void;
  selectAll: (sel: boolean) => void;
  clearAll: () => void;

  zone: DeliveryZone;
  setZone: (z: DeliveryZone) => void;

  subtotal: number;
  vat: (ratePct?: number) => number;
  delivery: () => number;
  total: (ratePct?: number) => number;

  selectedCount: number;
  itemCount: number;
  isDeliveryAvailable: boolean;
};

const CartCtx = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [zone, setZoneState] = useState<DeliveryZone>("");

  useEffect(() => {
    setLines(readCart());
    const savedZone = localStorage.getItem(
      "deliveryZone",
    ) as DeliveryZone | null;
    if (savedZone) setZoneState(savedZone);

    const onChange = () => setLines(readCart());
    window.addEventListener("storage", onChange);
    window.addEventListener("cartUpdated", onChange as EventListener);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener("cartUpdated", onChange as EventListener);
    };
  }, []);

  const persist = useCallback((next: CartLine[]) => {
    setLines(next);
    writeCart(next);
  }, []);

  const setZone = useCallback((z: DeliveryZone) => {
    setZoneState(z);
    localStorage.setItem("deliveryZone", z);
  }, []);

  const add = useCallback((item: AddInput, qty = 1) => {
    if (qty <= 0) return;
    setLines((prev) => {
      const next = [...prev];
      const i = next.findIndex((l) => l.id === item.id);
      if (i >= 0) {
        next[i] = {
          ...next[i],
          quantity: next[i].quantity + qty,
          selected: true,
        };
      } else {
        next.unshift({
          id: item.id,
          name: item.name,
          price: Number(item.price),
          imageUrl: item.imageUrl,
          quantity: qty,
          selected: true,
        });
      }
      writeCart(next);
      return next;
    });
    toast.success("Added to cart");
  }, []);

  const remove = useCallback(
    (id: number) => {
      persist(lines.filter((l) => l.id !== id));
      toast.success("Removed");
    },
    [lines, persist],
  );

  const setQty = useCallback(
    (id: number, qty: number) => {
      if (qty < 1) qty = 1;
      persist(lines.map((l) => (l.id === id ? { ...l, quantity: qty } : l)));
    },
    [lines, persist],
  );

  const toggle = useCallback(
    (id: number, sel?: boolean) => {
      persist(
        lines.map((l) =>
          l.id === id ? { ...l, selected: sel ?? !l.selected } : l,
        ),
      );
    },
    [lines, persist],
  );

  const selectAll = useCallback(
    (sel: boolean) => persist(lines.map((l) => ({ ...l, selected: sel }))),
    [lines, persist],
  );

  const clearAll = useCallback(() => {
    clearCart();
    setLines([]);
  }, []);

  const subtotal = useMemo(
    () =>
      lines
        .filter((l) => l.selected)
        .reduce((s, l) => s + l.price * l.quantity, 0),
    [lines],
  );

  const isDeliveryAvailable = zone !== "outside_dhaka";

  const delivery = useCallback(() => {
    if (subtotal === 0 || !zone) return 0;
    const fee = getDeliveryFee(zone);
    return fee ?? 0;
  }, [subtotal, zone]);

  const vat = useCallback(
    (ratePct = 5) => (subtotal * ratePct) / 100,
    [subtotal],
  );

  const total = useCallback(
    (ratePct = 5) => subtotal + vat(ratePct) + delivery(),
    [subtotal, vat, delivery],
  );

  const selectedCount = useMemo(
    () => lines.filter((l) => l.selected).length,
    [lines],
  );
  const itemCount = useMemo(
    () => lines.reduce((s, l) => s + l.quantity, 0),
    [lines],
  );

  return (
    <CartCtx.Provider
      value={{
        lines,
        add,
        remove,
        setQty,
        toggle,
        selectAll,
        clearAll,
        zone,
        setZone,
        subtotal,
        vat,
        delivery,
        total,
        selectedCount,
        itemCount,
        isDeliveryAvailable,
      }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within <CartProvider>");
  return ctx;
}
